import React, { useMemo, useState, useEffect } from "react";
import { Bar } from "react-chartjs-2";
import {
  Chart as ChartJS,
  BarElement,
  CategoryScale,
  LinearScale,
  Tooltip,
  Legend
} from "chart.js";
import styled from "styled-components";
import { Info } from "lucide-react";
import { Tooltip as InfoTooltip } from "react-tooltip";

ChartJS.register(BarElement, CategoryScale, LinearScale, Tooltip, Legend);

const months = [
  { value: 0, label: "Cały rok" },
  { value: 1, label: "Styczeń" },
  { value: 2, label: "Luty" },
  { value: 3, label: "Marzec" },
  { value: 4, label: "Kwiecień" },
  { value: 5, label: "Maj" },
  { value: 6, label: "Czerwiec" },
  { value: 7, label: "Lipiec" },
  { value: 8, label: "Sierpień" },
  { value: 9, label: "Wrzesień" },
  { value: 10, label: "Październik" },
  { value: 11, label: "Listopad" },
  { value: 12, label: "Grudzień" },
];

const parseValue = (v) => {
  const n = parseFloat(v);
  return isNaN(n) || n < 0 ? 0 : n;
};

const formatNumber = (value) =>
  Number(value).toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, " ");

// Purple scheme for Zakupy
const PURPLE = "#9848c7";        // Usługi
const PURPLE_LIGHT = "#cdb0ea";  // Inne
const PURPLE_DARK = "#6f2aa7";   // highlight for max row

// === Styled ===
const ChartTitle = styled.div`
  font-family: Arial, sans-serif;
  font-size: 12px;
  font-style: normal;
  font-weight: bold;
  color: #666;
  margin-bottom: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
`;

const InfoIconWrapper = styled.div`
  cursor: pointer;
  color: #666;
  transition: color 0.3s;
  &:hover { color: #000; }
  svg { width: 18px; height: 18px; }
`;

const TopContractorsByTransactionTypeZakupy = ({ data = [] }) => {
  const [selectedYear, setSelectedYear] = useState(null);
  const [selectedMonth, setSelectedMonth] = useState(0);

  const availableYears = useMemo(
    () => [...new Set(data.map((row) => row.Rok?.toString()))].filter(Boolean).sort(),
    [data]
  );

  useEffect(() => {
    if (availableYears.length > 0 && !selectedYear) {
      setSelectedYear(availableYears.includes("2024") ? "2024" : availableYears[0]);
    }
  }, [availableYears, selectedYear]);

  const topContractors = useMemo(() => {
    if (!selectedYear) return [];

    const contractorTotals = {};

    data
      .filter(
        (row) =>
          row.Rok?.toString() === selectedYear &&
          (selectedMonth === 0 || parseInt(row.Miesiac) === selectedMonth)
      )
      .forEach((row) => {
        const id = row.NrDostawcy?.trim();
        const name = row.NazwaDostawcy?.trim();
        if (!id || !name) return;

        if (!contractorTotals[id]) {
          contractorTotals[id] = { name, service: 0, other: 0 };
        }

        // Total purchase value
        const total = parseValue(row.K_42) + parseValue(row.K_43);

        // Swap this heuristic with your real "service" rule/flag when available
        const serviceAmount = total * 0.15;
        const otherAmount = Math.max(0, total - serviceAmount);

        contractorTotals[id].service += serviceAmount;
        contractorTotals[id].other += otherAmount;
      });

    return Object.entries(contractorTotals)
      .map(([contractorID, v]) => ({
        contractorID,
        name: v.name,
        service: v.service,
        other: v.other,
        total: v.service + v.other,
      }))
      .sort((a, b) => b.total - a.total)
      .slice(0, 10);
  }, [data, selectedYear, selectedMonth]);

  const labels = topContractors.map((c) =>
    c.name?.length > 30 ? `${c.name.slice(0, 30)}…` : c.name
  );

  const maxTotal = topContractors.length
    ? Math.max(...topContractors.map((c) => c.total))
    : 0;

  // Per-row colors (max row turns darker purple)
  const serviceBG = topContractors.map((c) => (c.total === maxTotal && maxTotal > 0 ? PURPLE_DARK : PURPLE));
  const serviceBorder = serviceBG;
  const otherBG = topContractors.map((c) => (c.total === maxTotal && maxTotal > 0 ? PURPLE_DARK : PURPLE_LIGHT));
  const otherBorder = otherBG;

  const chartData = {
    labels,
    datasets: [
      {
        label: "Usługi",
        data: topContractors.map((c) => c.service),
        backgroundColor: serviceBG,
        borderColor: serviceBorder,
        borderWidth: 1,
        borderRadius: 4,
      },
      {
        label: "Inne",
        data: topContractors.map((c) => c.other),
        backgroundColor: otherBG,
        borderColor: otherBorder,
        borderWidth: 1,
        borderRadius: 4,
      },
    ],
  };

  if (!selectedYear || availableYears.length === 0) {
    return <div style={{ textAlign: "center" }}>Ładowanie danych...</div>;
  }

  return (
    <div style={{ width: "100%", height: "100%", margin: "0 auto", textAlign: "center" }}>
      <ChartTitle>
        Top 10 dostawców – Usługi vs Inne
        <InfoIconWrapper data-tooltip-id="top-suppliers-purchases-tooltip">
          <Info />
        </InfoIconWrapper>
      </ChartTitle>

      <InfoTooltip
        id="top-suppliers-purchases-tooltip"
        place="right"
        style={{ fontSize: "12px", fontFamily: "Arial, sans-serif", color: "#fff" }}
        content={
          <>
            <strong>Najwięksi dostawcy</strong><br />
            Usługi = fiolet, Inne = jasny fiolet.<br />
            Rekord o największej łącznej wartości wyróżniony ciemniejszym fioletem.<br />
            (Obecnie Usługi = 15% wartości; podmień na realną flagę, jeśli masz pole.)
          </>
        }
      />

      {/* Fill the card: parent controls the height */}
      <div style={{ width: "100%", height: "calc(100% - 130px)", minHeight: 240 }}>
        <Bar
          data={chartData}
          options={{
            responsive: true,
            maintainAspectRatio: false,
            indexAxis: "y",
            plugins: {
              legend: {
                position: "bottom",
                labels: {
                  // Legend chips locked to purple scheme
                  generateLabels: (chart) => {
                    const ds = chart.data.datasets;
                    return ds.map((d, i) => ({
                      text: d.label,
                      fillStyle: i === 0 ? PURPLE : PURPLE_LIGHT,
                      strokeStyle: i === 0 ? PURPLE : PURPLE_LIGHT,
                      lineWidth: 1,
                      hidden: !chart.isDatasetVisible(i),
                      datasetIndex: i,
                    }));
                  },
                },
              },
              tooltip: {
                backgroundColor: "#333",
                titleColor: "#fff",
                bodyColor: "#fff",
                callbacks: {
                  label: (ctx) => `${ctx.dataset.label}: ${formatNumber(ctx.raw)}`,
                },
              },
            },
            layout: { padding: { left: 4, right: 8, top: 4, bottom: 4 } },
            scales: {
              x: {
                stacked: true,
                title: {
                  display: true,
                  text: "Kwota",
                  color: "#666",
                  font: { size: 12, weight: "bold" },
                },
                ticks: { callback: (v) => formatNumber(v) },
                grid: { drawBorder: false },
              },
              y: {
                stacked: true,
                ticks: { autoSkip: true, maxTicksLimit: Math.min(12, topContractors.length || 1) },
                grid: { drawBorder: false },
              },
            },
          }}
        />
      </div>
    </div>
  );
};

export default TopContractorsByTransactionTypeZakupy;
